import BreadCrumb from '@react/shared/components/BreadCrumb';
import Footer from '@react/shared/components/Footer';
import Image from '@react/shared/components/Image';
import { CMSRequestInfo } from '@react/shared/types/requestInfo';
import {
  Article,
  Reviewer,
  ReviewerBody,
} from '@react/shared/types/usePrefetch';
import { RESOURCE_CENTER_PATH } from '@sly/core/constants/dashboardAppPaths';
import { RESOURCE_CENTER_REVIEWER_INDEX_PATH } from '@sly/core/constants/dashboardAppPaths';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { parse } from 'query-string';
import React, { useEffect } from 'react';
import Arrow from 'sly/icons/Arrow';
import { assetPath } from 'sly/lib/path';
import { useLocation } from 'sly/lib/react-router';
import AuthorArticles from 'sly/resourceCenter/components/AuthorArticles';
import Header from 'sly/resourceCenter/components/Header';
import Helmet from 'sly/resourceCenter/components/Helmet';
import ResourceCenterChatBotWrapper from 'sly/resourceCenter/components/ResourceCenterChatBotWrapper';
import SubscribeEmail from 'sly/resourceCenter/components/SubscribeEmail';
import { ARTICLES_RANGE_FOR_PAGINATION } from 'sly/resourceCenter/helpers';
import { usePrefetch } from 'sly/services/api/prefetch';
import { pageNames, useEvents } from 'sly/services/events';
import { Link } from 'sly/system';
import Heading from 'sly/system/Heading';

import { pagePadding } from '../../styles';

interface ReviewerPageParams {
  params: {
    slug: string;
  };
}

const ReviewerArticlesWrapper = ({
  reviewer,
  pageNumber,
}: {
  reviewer: ReviewerBody;
  pageNumber: string;
}) => {
  const { firstName, slug } = reviewer;
  const router = useRouter();

  const { requestInfo }: { requestInfo: CMSRequestInfo<Article> } = usePrefetch(
    'getArticle',
    {
      'filters[reviewer][slug][$eq]': encodeURIComponent(slug),
      'pagination[pageSize]': ARTICLES_RANGE_FOR_PAGINATION,
      'pagination[page]': router.query?.['page-number'] || 1,
      populate: '*',
      sort: 'id:desc',
    }
  );

  if (!requestInfo?.body?.data) {
    return null;
  }

  return (
    <div
      className='mx-auto md:w-[42.5rem] lg:w-[64.5rem]'
      data-tp-id='gen-div-54e789e8-5144-4d6f-96c2-e590c610e3ab'
    >
      <AuthorArticles
        articlesCount={requestInfo.body.meta.pagination.total}
        slug={slug}
        firstName={firstName}
        pageNumber={pageNumber}
        articlesList={requestInfo.body.data}
        type='reviewer'
      />
    </div>
  );
};

const ReviewerPage = ({ params }: ReviewerPageParams) => {
  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.ReviewerProfile, {});
  }, []);

  const location = useLocation();
  const { slug } = params;
  const { 'page-number': pageNumber } = parse(location.search);

  // Note : Slug should always be url encoded, encode it while redirecting
  const { requestInfo }: { requestInfo: CMSRequestInfo<Reviewer> } =
    usePrefetch('getReviewer', {
      'filters[slug][$eq]': encodeURIComponent(slug),
      populate: '*',
    });

  if (!requestInfo.hasFinished) {
    return (
      <div
        className='flex h-[100vh]  flex-col items-center justify-center'
        data-tp-id='gen-div-db0ba05a-f3e8-4b0e-a99b-0569470d024a'
      >
        <Image
          width='250'
          height='250'
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
          alt='loader'
          data-tp-id='gen-Image-589929e5-d193-47d3-9b1d-0793ce204a13'
        />
      </div>
    );
  }

  if (requestInfo.result?.data?.length === 0) {
    return null;
  }

  const reviewer = requestInfo?.result?.data[0];
  const {
    slug: _slug,
    fullName,
    shortDescription,
    fullDescription,
    img,
  } = reviewer || {};

  const paths = [
    {
      label: 'Resource Center Home',
      path: RESOURCE_CENTER_PATH,
    },
    {
      label: 'Reviewers',
      path: RESOURCE_CENTER_PATH + '/reviewers',
    },
    {
      label: fullName,
      path: '',
    },
  ];

  return (
    <>
      <ResourceCenterChatBotWrapper>
        <Helmet
          path={`${RESOURCE_CENTER_PATH}/reviewer/${_slug}`}
          title={fullName}
          shortDescription={shortDescription}
          isReviewerPage
          reviewer={reviewer}
        />
        <Header />
        <div className={`${pagePadding} mx-auto mt-6 md:w-[42.5rem]`}>
          <BreadCrumb items={paths} />
        </div>
        <div
          className={cx(
            pagePadding,
            'flex',
            'items-center',
            'flex-col',
            'px-0 pt-6 pb-20'
          )}
          data-tp-id='gen-div-122037d2-c403-4302-858e-24a2b25ac994'
        >
          <div
            className='aspect-w-1 h-[15rem] w-[15rem] overflow-hidden rounded-[50%]'
            data-tp-id='gen-div-b388a741-7ca0-4c9e-a0ef-2c59f6c68af8'
          >
            <Image
              path={img?.path}
              alt={img?.alternativeText}
              sizes='(max-width: 727px) 160px, 240px'
              layout='fill'
              data-tp-id='gen-Image-b330803d-3cec-4cbd-a72d-fd39e2a81cbe'
              objectFit='cover'
            />
          </div>
          <Heading
            as='h1'
            className='mb-6 pt-l'
            data-tp-id='gen-Heading-15e57d28-1d5c-42ec-b2d7-4d99edf1d964'
            font='font-t-xl'
          >
            {fullName}
          </Heading>
          <div
            dangerouslySetInnerHTML={{ __html: fullDescription }}
            className='dynamic-editor mx-auto md:w-[42.5rem]'
          />
          <div
            className='left-0 right-auto mt-12 w-full max-w-[42.5rem]'
            data-tp-id='gen-div-404609ce-658f-4517-8e32-62434da3a887'
          >
            <Link
              className='font-b-l'
              to={RESOURCE_CENTER_REVIEWER_INDEX_PATH}
              data-tp-id='gen-Link-f03e2114-80a7-4e05-9cf4-4b7efababb10'
            >
              <div
                className='flex underline'
                data-tp-id='gen-div-95a4d2cd-ccf8-4500-926d-d24f1de37f49'
              >
                <div data-tp-id='gen-div-c80f10d4-2208-44ec-82a0-e2956081a57e'>
                  See other Seniorly reviewers{' '}
                </div>
                <div
                  className='flex flex-col justify-center'
                  data-tp-id='gen-div-8f906a4e-cf26-4ee4-88e6-ff1382bd110c'
                >
                  <Arrow className='rotate-90' />
                </div>
              </div>
            </Link>
          </div>
        </div>
        <hr />
        <div
          className={cx(pagePadding, 'mt-xxl md:mt-xxxl')}
          data-tp-id='gen-div-0130c3b4-5971-4610-8840-8f98dc75c060'
        >
          <ReviewerArticlesWrapper
            reviewer={reviewer}
            pageNumber={pageNumber as string}
          />
        </div>
        <SubscribeEmail />
        <Footer />
      </ResourceCenterChatBotWrapper>
    </>
  );
};

ReviewerPage.displayName = 'Reviewer';

export default ReviewerPage;
